import type { AdditionalNote, Job, JobTypeName } from "@stryker/core/types/jobs";
import { useQuery } from "@tanstack/react-query";
import { additionalNotesQueryOptions } from "@/hooks/useAdditionalNotes";
import { getCompositeJobInfoBlocks } from "@/hooks/useCompositeJobInfoBlocks";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { getMechanicalJobInfoBlocks } from "@/hooks/useMechanicalJobInfoBlocks";

type AdditionalNotesLookup = {
  notes: Pick<AdditionalNote, "id" | "name">[];
  notesById: Record<number, string>;
};

export default function useGetJobInfoBlocks({ job }: { job?: Job }) {
  const isJobTypeName = (value: string | null | undefined): value is JobTypeName =>
    value === "mechanical" || value === "composite";
  const jobType = isJobTypeName(job?.job_type) ? job.job_type : undefined;
  const {
    repairSystemOptions,
    surfacePreparationOptions,
    allKits,
    componentMaterialLabelById,
    enclosureMaterialLabelById,
  } = useConfigOptions();
  const { data: additionalNotesData } = useQuery({
    ...additionalNotesQueryOptions(jobType),
    select: (data): AdditionalNotesLookup => {
      const notes = data.additional_notes ?? [];
      const notesById = notes.reduce<Record<number, string>>((acc, note) => {
        acc[note.id] = note.name;
        return acc;
      }, {});

      return { notes, notesById };
    },
  });

  if (!job) {
    return [];
  }

  if (job.job_type === "mechanical") {
    return getMechanicalJobInfoBlocks({
      job,
      additionalNotesData,
      componentMaterialLabelById,
      enclosureMaterialLabelById,
    });
  }

  return getCompositeJobInfoBlocks({
    job,
    additionalNotesData,
    repairSystemOptions,
    surfacePreparationOptions,
    allKits,
    componentMaterialLabelById,
  });
}
